// Design tokens for SummarizeX Dashboard
// Based on the beautiful purple gradient theme shown in the UI

export const commonTokens = {
  // Color palette - matching the purple gradient theme
  colors: {
    // Primary purple gradient colors
    primary: '#6366f1', // Indigo primary
    primaryDark: '#4f46e5', // Darker shade of primary for hover states
    primaryLight: '#818cf8', // Lighter shade of primary for gradients
    accent: '#8b5cf6',  // Purple accent
    
    // Status colors
    success: '#10b981', // Emerald green
    error: '#ef4444',   // Red
    warning: '#f59e0b', // Amber
    info: '#3b82f6',    // Blue 
    yellow: '#f59e0b', // Amber
    failed:"#2FA4D7",
    
    // Additional colors for variety
    secondary: '#64748b', // Slate
    secondaryDark: '#475569', // Darker slate
    
    // Extended color palette for UI components
    purple: '#9c27b0',        // Material purple
    purpleLight: '#9C27B0',   // Light purple variant
    green: '#4caf50',         // Material green
    greenDark: '#2e7d32',     // Dark green
    greenLight: '#34A853',    // Light green
    orange: '#ff9800',        // Material orange
    orangeDeep: '#FF9800',    // Deep orange
    red: '#f44336',           // Material red
    redDark: '#c62828',       // Dark red
    redError: '#dc2626',      // Error red
    redErrorDark: '#b91c1c',  // Dark error red
    blue: '#2196f3',          // Material blue
    blueLight: '#007AFF',     // Light blue
    gray: '#9e9e9e',          // Material gray
    grayLight: '#e0e0e0',     // Light gray
    grayMedium: '#78909C',    // Medium gray
    grayBackground: '#f0f0f0', // Background gray
    white: '#ffffff',         // Pure white
    black: '#000000',         // Pure black
    gold: '#FFD700',          // Gold color
    
    // Android/iOS specific colors
    androidGreen: '#3DDC84',  // Android green
    
    // Text colors (semantic)
    textDark: '#2d3748',      // Dark text
    
    // Gradient combinations
    gradientPrimary: 'linear-gradient(135deg, #6366f1 0%, #9C27B0 100%)',
    gradientSecondary: 'linear-gradient(145deg, #667eea 0%, #764ba2 100%)',
    gradientSuccess: 'linear-gradient(135deg, #2E7D32 0%, #FF9800 100%)',
    gradientDanger: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    gradientWarning: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 100%)',
    gradientCard: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    gradientBackground: 'linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)',
    gradientNotification: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    gradientButton: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    gradientButtonHover: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
    gradientSidebar: 'linear-gradient(90deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
    gradientGlass: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
  },

  // Semantic color tokens for consistent theming
  semantic: {
    // Text colors
    textPrimary: '#1f2937',      // Dark gray for primary text
    textSecondary: '#6b7280',    // Medium gray for secondary text
    textTertiary: '#9ca3af',     // Light gray for tertiary text
    
    // Background colors
    background: '#ffffff',           // Pure white background
    backgroundSecondary: '#f9fafb',  // Very light gray
    backgroundTertiary: '#f3f4f6',   // Light gray for subtle backgrounds
    surface: '#ffffff',              // Surface color for paper/cards
    
    // Border and divider colors
    border: '#e5e7eb',          // Light gray border
    borderLight: '#f3f4f6',     // Very light border
    borderDark: '#d1d5db',      // Darker border for emphasis
    
    // Status background colors with transparency
    successBackground: '#e8f5e8',  // Light green background
    errorBackground: '#ffebee',    // Light red background
    warningBackground: '#fff3e0',  // Light orange background
    infoBackground: '#e3f2fd',     // Light blue background
  },

  // Border radius tokens for consistent rounded corners
  borderRadius: {
    sm: '4px',   // Small radius
    md: '8px',   // Medium radius (most common)
    lg: '12px',  // Large radius for cards and containers
    xl: '16px',  // Extra large radius
    xxl: '24px', // For major components
    full: '50%', // Circular elements
  },

  // Shadow tokens for depth and elevation
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    xxl: '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    inner: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
    
    // Custom shadows with specific colors
    primaryShadow: '0 8px 32px rgba(99, 102, 241, 0.25)',
    successShadow: '0 2px 8px rgba(46, 125, 50, 0.30)',
    orangeShadow: '0 2px 8px rgba(255, 152, 0, 0.30)',
  },

  // Spacing tokens for consistent layout
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
  },

  // Typography tokens
  typography: {
    fontFamily: {
      primary: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      mono: '"Fira Code", "Monaco", "Consolas", monospace',
    },
    fontSize: {
      xs: '0.75rem',   // 12px
      sm: '0.875rem',  // 14px
      md: '1rem',      // 16px
      base: '1rem',    // 16px (alias for md)
      lg: '1.125rem',  // 18px
      xl: '1.25rem',   // 20px
      xxl: '1.5rem',   // 24px
      '2xl': '1.5rem', // 24px (alias for xxl)
      xxxl: '2rem',    // 32px
      '3xl': '2rem',   // 32px (alias for xxxl)
      '4xl': '2.5rem', // 40px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      semiBold: 600,   // Alias for semibold
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },

  // Animation and transition tokens
  transitions: {
    fast: '150ms ease-in-out',
    normal: '200ms ease-in-out',
    slow: '300ms ease-in-out',
  },

  // Z-index tokens for layering
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
};

// Export individual token categories for convenience
export const { colors, semantic, borderRadius, shadows, spacing, typography, transitions, zIndex } = commonTokens;

// Default export
export default commonTokens;
